import React, { useEffect, useState } from 'react'
import { supabase } from '../../lib/supabase'
import { Card, Badge } from '../../components/UI'
import { BarChart3, Monitor, Globe, Clock, RefreshCw, Filter, Activity, Users, AlertTriangle, ChevronDown, ChevronUp, Trash2, Database } from 'lucide-react'
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, PieChart, Pie, Legend } from 'recharts'
import { toast } from 'sonner'

const COLORS = ['#00a8ff', '#00ff88', '#ffcc00', '#ff3333', '#9b59b6', '#34495e', '#e67e22', '#2ecc71']

export default function TelemetryPage() {
    const [kpis, setKpis] = useState({ sessions: 0, active_users: 0, avg_session_duration: 0, error_rate: 0 })
    const [trafficData, setTrafficData] = useState([])
    const [moduleData, setModuleData] = useState([])
    const [deviceData, setDeviceData] = useState([])
    const [events, setEvents] = useState([])
    const [isEventsCollapsed, setIsEventsCollapsed] = useState(true)

    const [licenses, setLicenses] = useState([])
    const [filterLicense, setFilterLicense] = useState('all')
    const [filterDays, setFilterDays] = useState(7)
    const [loading, setLoading] = useState(true)

    useEffect(() => { fetchLicenses(); fetchData() }, [filterLicense, filterDays])

    const fetchLicenses = async () => {
        const { data } = await supabase.from('licenses').select('id, client_name')
        setLicenses(data || [])
    }

    const fetchData = async () => {
        setLoading(true)
        try {
            const lid = filterLicense === 'all' ? null : filterLicense

            // Ejecutar todas las consultas en paralelo
            const [kpiRes, trafficRes, modulesRes, devicesRes, eventsRes] = await Promise.all([
                supabase.rpc('tower_get_kpi_summary', { p_days: filterDays, p_license_id: lid }),
                supabase.rpc('tower_get_traffic_hourly', { p_days: filterDays, p_license_id: lid }),
                supabase.rpc('tower_get_module_usage', { p_days: filterDays, p_license_id: lid }),
                supabase.rpc('tower_get_device_stats', { p_days: filterDays, p_license_id: lid }),
                supabase.from('telemetry_events')
                    .select('*, licenses(client_name)')
                    .in('event_type', ['session_start', 'pageview', 'error', 'app_crash'])
                    .order('created_at', { ascending: false })
                    .limit(50)
            ])

            if (kpiRes.data) setKpis(kpiRes.data)
            if (trafficRes.data) setTrafficData(trafficRes.data)
            if (modulesRes.data) setModuleData(modulesRes.data)
            if (devicesRes.data) setDeviceData(devicesRes.data)
            if (eventsRes.data) {
                // Filtrar localmente por licencia si es necesario (aunque el RPC ya debería filtrar, la tabla raw no)
                const filteredEvents = lid ? eventsRes.data.filter(e => e.license_id === lid) : eventsRes.data
                setEvents(filteredEvents)
            }

        } catch (e) {
            console.error('[Analytics]', e)
        }
        setLoading(false)
    }

    const handlePurge = async () => {
        if (!window.confirm('¿Estás seguro de eliminar el historial antiguo? Esta acción no se puede deshacer.')) return

        const toastId = toast.loading('Purgando datos antiguos...')
        try {
            const { data, error } = await supabase.rpc('tower_purge_analytics', {
                p_retention_days: 30,
                p_license_id: filterLicense === 'all' ? null : filterLicense
            })

            if (error) throw error
            toast.success(`Limpieza completada: ${data.deleted_rows} registros eliminados`, { id: toastId })
            fetchData()
        } catch (e) {
            console.error(e)
            toast.error('Error al purgar datos: ' + e.message, { id: toastId })
        }
    }

    const formatDuration = (ms) => {
        if (!ms) return '0s'
        const sec = Math.round(ms / 1000)
        return sec > 60 ? `${Math.floor(sec / 60)}m ${sec % 60}s` : `${sec}s`
    }

    return (
        <div className="p-6 max-w-7xl mx-auto pb-20 fade-in">
            {/* Header & Filters */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
                <div>
                    <h1 className="text-3xl font-bold text-white mb-1">Panel de Inteligencia</h1>
                    <p className="text-gray-500 text-sm">Estadísticas y salud del sistema en tiempo real.</p>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                    <select
                        className="bg-charcoal border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-neon-blue"
                        value={filterDays} onChange={e => setFilterDays(parseInt(e.target.value))}
                    >
                        <option value={1}>Últimas 24h</option>
                        <option value={7}>Últimos 7 días</option>
                        <option value={30}>Últimos 30 días</option>
                    </select>

                    <select
                        className="bg-charcoal border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-neon-blue"
                        value={filterLicense} onChange={e => setFilterLicense(e.target.value)}
                    >
                        <option value="all">🏢 Todos los Proyectos</option>
                        {licenses.map(l => <option key={l.id} value={l.id}>{l.client_name}</option>)}
                    </select>

                    <button onClick={fetchData} className="bg-neon-green/10 text-neon-green hover:bg-neon-green hover:text-black px-4 py-2 rounded-lg text-sm font-bold transition-all flex items-center gap-2">
                        <RefreshCw size={16} className={loading ? 'animate-spin' : ''} /> Actualizar
                    </button>
                </div>
            </div>

            {/* KPI Grid */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                <KPICard icon={Activity} label="Sesiones Totales" value={kpis.sessions} color="text-blue-400" />
                <KPICard icon={Users} label="Usuarios Únicos" value={kpis.active_users} color="text-purple-400" />
                <KPICard icon={Clock} label="Tiempo Promedio" value={formatDuration(kpis.avg_session_duration)} color="text-neon-green" />
                <KPICard icon={AlertTriangle} label="Tasa de Error" value={`${kpis.error_rate}%`} color="text-red-400" sub={kpis.error_rate > 5 ? 'Crítico' : 'Normal'} />
            </div>

            {/* Main Charts */}
            <div className="grid lg:grid-cols-3 gap-6 mb-8">
                {/* Traffic Chart (2 cols) */}
                <Card className="lg:col-span-2 min-h-[350px] flex flex-col">
                    <h3 className="text-lg font-bold text-white mb-6 flex items-center gap-2"><BarChart3 size={18} /> Tráfico por Hora</h3>
                    <div className="flex-1 w-full min-h-[250px]">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={trafficData}>
                                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" vertical={false} />
                                <XAxis dataKey="hour" stroke="#666" tick={{ fill: '#888', fontSize: 12 }} />
                                <Tooltip
                                    contentStyle={{ backgroundColor: '#1a1a1a', border: '1px solid #333', borderRadius: '8px', color: '#fff' }}
                                    itemStyle={{ color: '#00a8ff' }}
                                    cursor={{ fill: 'rgba(255,255,255,0.05)' }}
                                />
                                <Bar dataKey="sessions" fill="#00a8ff" radius={[4, 4, 0, 0]} barSize={20} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </Card>

                {/* Modules Chart (1 col) */}
                <Card className="min-h-[350px] flex flex-col">
                    <h3 className="text-lg font-bold text-white mb-6 flex items-center gap-2"><Globe size={18} /> Uso por Módulo</h3>
                    <div className="flex-1 w-full min-h-[250px]">
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Pie
                                    data={moduleData}
                                    cx="50%" cy="50%"
                                    innerRadius={60} outerRadius={80}
                                    paddingAngle={5}
                                    dataKey="value"
                                >
                                    {moduleData.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                    ))}
                                </Pie>
                                <Tooltip contentStyle={{ backgroundColor: '#1a1a1a', border: '1px solid #333', borderRadius: '8px' }} />
                                <Legend />
                            </PieChart>
                        </ResponsiveContainer>
                    </div>
                </Card>
            </div>

            {/* Bottom Grid: Devices & Audit */}
            <div className="grid lg:grid-cols-3 gap-6 mb-8">
                {/* Devices Table */}
                <Card>
                    <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2"><Monitor size={18} /> Dispositivos</h3>
                    <div className="space-y-4">
                        {deviceData.filter(d => d.type === 'browser').slice(0, 5).map((d, i) => (
                            <div key={i} className="flex justify-between items-center text-sm">
                                <div className="flex items-center gap-2">
                                    <div className="w-2 h-2 rounded-full" style={{ backgroundColor: COLORS[i % COLORS.length] }}></div>
                                    <span className="text-gray-300">{d.name}</span>
                                </div>
                                <span className="font-mono text-white bg-white/5 px-2 py-0.5 rounded">{d.count}</span>
                            </div>
                        ))}
                    </div>
                </Card>

                {/* Raw Events List (Collapsible) */}
                <Card className="lg:col-span-2 p-0 overflow-hidden text-white">
                    <div
                        className="px-6 py-4 border-b border-white/5 flex justify-between items-center cursor-pointer hover:bg-white/5 transition-colors"
                        onClick={() => setIsEventsCollapsed(!isEventsCollapsed)}
                    >
                        <div className="flex items-center gap-2">
                            <h3 className="text-lg font-bold text-white">Eventos Recientes</h3>
                            <Badge status="info" label="En vivo" />
                        </div>
                        {isEventsCollapsed ? <ChevronDown className="text-gray-400" /> : <ChevronUp className="text-gray-400" />}
                    </div>

                    {!isEventsCollapsed && (
                        <div className="overflow-x-auto animate-in slide-in-from-top-2">
                            <table className="w-full text-left">
                                <thead className="bg-white/5 text-xs text-gray-500 uppercase">
                                    <tr>
                                        <th className="px-6 py-3">Evento</th>
                                        <th className="px-6 py-3">Proyecto</th>
                                        <th className="px-6 py-3">Detalles</th>
                                        <th className="px-6 py-3">Hora</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-white/5 text-sm">
                                    {events.map(evt => (
                                        <tr key={evt.id} className="hover:bg-white/5 transition-colors">
                                            <td className="px-6 py-3"><Badge status={evt.event_type} /></td>
                                            <td className="px-6 py-3 text-white font-medium">{evt.licenses?.client_name || 'Desconocido'}</td>
                                            <td className="px-6 py-3 text-gray-400 font-mono text-xs max-w-[200px] truncate">
                                                {evt.payload?.url || evt.payload?.message || JSON.stringify(evt.payload)}
                                            </td>
                                            <td className="px-6 py-3 text-gray-500 whitespace-nowrap">
                                                {new Date(evt.created_at).toLocaleTimeString()}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </Card>
            </div>

            {/* Data Management Section */}
            <div className="border-t border-white/10 pt-8 mt-8">
                <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                    <Database size={20} className="text-neon-blue" /> Gestión de Datos
                </h2>
                <Card className="flex flex-col md:flex-row items-center justify-between gap-4 bg-white/5 border-white/10">
                    <div>
                        <h3 className="text-white font-bold text-lg">Optimización y Limpieza</h3>
                        <p className="text-sm text-gray-400 mt-1 max-w-2xl">
                            Elimina registros de telemetría antiguos para mantener la base de datos rápida y ligera.
                            Se conservarán los datos de los últimos 30 días.
                        </p>
                    </div>
                    <button
                        onClick={handlePurge}
                        className="bg-red-500/10 text-red-400 hover:bg-red-500 hover:text-white px-5 py-3 rounded-lg text-sm font-bold transition-all flex items-center gap-2 border border-red-500/20 whitespace-nowrap"
                    >
                        <Trash2 size={18} /> Limpiar Historial (+30 días)
                    </button>
                </Card>
            </div>
        </div>
    )
}

function KPICard({ icon: Icon, label, value, color, sub }) {
    return (
        <Card className="flex flex-col items-center justify-center py-6 hover:bg-white/5 transition-colors border-l-4 border-transparent hover:border-neon-blue">
            <Icon size={28} className={`${color} mb-3`} />
            <span className="text-4xl font-bold text-white mb-1 tracking-tight">{value}</span>
            <span className="text-xs text-gray-400 uppercase tracking-widest font-semibold">{label}</span>
            {sub && <span className="mt-2 text-xs px-2 py-0.5 rounded bg-white/10 text-white animate-pulse">{sub}</span>}
        </Card>
    )
}
